/**
 * LifeSigns - a kill-team brother's Solo lane (Squad Mode stage 8, board 10i): his heart as a wide ECG in the health
 * ramp's colour, his mind as a narrow multicoloured EEG over it ("warp" gradient), both flowing down at 60 px/s - one
 * CSS translate a loop, never a per-frame rebuild. Critical damage wakes the second heart: a second ECG beside the
 * first. Dead, every line goes flat and the lane fades with him. With motion off, one still strip of the rhythm.
 *
 * Drawn in the lane's frame units; `unit` is frame units per screen px (the traces are made in screen px).
 */
import { memo, useMemo } from 'react';
import { LOOP, ecgPath, eegPath } from '../../../../engine/plate/traces';
import type { LifeSigns as Signs } from '../../../../engine/plate/lifeSigns';
import type { Frame } from '../../../../engine/plate/calloutLayout';
import styles from '../../../../styles/PlateKillTeam.module.css';

interface Props {
  u: string;
  lane: Frame;
  /** Frame units per screen px. */
  unit: number;
  signs: Signs;
  /** In Squad Mode: the heart alone, slim and faint at the lane's right edge, beside the rail (the hand-over). */
  slim?: boolean;
}

/** Copies of one loop, stacked down the lane's height and one more, so the scroll never shows an end. */
function Strip({ d, copies, className }: { d: string; copies: number; className: string }) {
  return (
    <>
      {Array.from({ length: copies }, (_, i) => (
        <path key={i} className={className} d={d} transform={`translate(0 ${(i - 1) * LOOP})`} />
      ))}
    </>
  );
}

/** The four traces for these signs, made once per signs and lane width (a slim heart has no mind and no ghost). */
function useTraces(signs: Signs, widthPx: number, slim: boolean) {
  const amp = widthPx * (slim ? 0.1 : 0.32);
  const spread = widthPx * 0.1;
  return useMemo(
    () => ({
      amp,
      ecg: ecgPath(signs.heart, amp),
      second: signs.second ? ecgPath(signs.heart, amp * 0.6, 2) : null,
      eeg: slim ? null : eegPath(signs.mind, spread),
      ghost: signs.mind.ghost && !slim ? eegPath(signs.mind, spread, 0.37) : null,
    }),
    [signs, amp, spread, slim],
  );
}

type Traces = ReturnType<typeof useTraces>;

/** The traces stacked and flowing: the second heart beside the first, the mind (and its ghost) over them. */
function Flow({ t, copies }: { t: Traces; copies: number }) {
  return (
    <g className={styles.flow}>
      {t.second && (
        <g transform={`translate(${-t.amp * 0.9} 0)`}>
          <Strip d={t.second} copies={copies} className={styles.ecgSecond} />
        </g>
      )}
      <Strip d={t.ecg} copies={copies} className={styles.ecg} />
      {t.ghost && <Strip d={t.ghost} copies={copies} className={styles.ghost} />}
      {t.eeg && <Strip d={t.eeg} copies={copies} className={styles.eeg} />}
    </g>
  );
}

export default memo(function LifeSigns({ u, lane, unit, signs, slim = false }: Props) {
  const traces = useTraces(signs, lane.w / unit, slim);
  const copies = Math.ceil(lane.h / unit / LOOP) + 2;
  const clip = `${u}-lane`;
  return (
    <g
      className={slim ? styles.slim : styles.lifeSigns}
      data-testid={slim ? 'plate-life-signs-slim' : 'plate-life-signs'}
      data-rhythm={signs.heart.rhythm}
      data-wave={signs.mind.wave}
      data-second={signs.second || undefined}
      data-trains={signs.mind.trains || undefined}
    >
      <clipPath id={clip}>
        <rect x={lane.x} y={lane.y} width={lane.w} height={lane.h} />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <g transform={`translate(${lane.x + lane.w * (slim ? 0.88 : 0.5)} ${lane.y}) scale(${unit})`}>
          <Flow t={traces} copies={copies} />
        </g>
      </g>
    </g>
  );
});
